// hooks/useExpenses.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API } from "../../config";
import { useSelector } from "react-redux";

// Create Expense
const createExpense = async (expense, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };
    const { data } = await axios.post(
        `${API}/expenses-create/${userInfo._id}`,
        expense,
        config
    );
    return data;
};

export const useCreateExpense = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (expense) => createExpense(expense, userInfo),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["appointments"] });
        },
    });
};

// List Expenses
export const useListExpenses = ({
    page,
    limit,
    doctor,
    startDate,
    endDate,
}) => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ["expenses", { page, limit, doctor, startDate, endDate }],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (page) params.append("page", page);
            if (limit) params.append("limit", limit);
            if (doctor) params.append("doctor", doctor);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const { data } = await axios.get(
                `${API}/expenses-list/${userInfo._id}?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                }
            );
            return data;
        },
    });
};

// Delete Expense
const deleteExpense = async (id, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };
    const { data } = await axios.delete(
        `${API}/expenses-remove/${id}/${userInfo._id}`,
        config
    );
    return data;
};

export const useDeleteExpense = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (id) => deleteExpense(id, userInfo),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["appointments"] });
        },
    });
};

// Update Expense
const updateExpense = async (expense, userInfo) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userInfo.token}`,
        },
    };
    const { data } = await axios.put(
        `${API}/expenses-update/${expense._id}/${userInfo._id}`,
        expense,
        config
    );
    return data;
};

export const useUpdateExpense = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (expense) => updateExpense(expense, userInfo),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["appointments"] });
            queryClient.setQueryData(["expenseDetails", data._id], data);
        },
    });
};

// Expense Details
export const useExpenseDetails = (id) => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ["expenseDetails", id],
        queryFn: async () => {
            const { data } = await axios.get(
                `${API}/expenses-detail/${id}/${userInfo._id}`,
                {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                }
            );
            return data;
        },
        enabled: !!id,
    });
};

// Paid Enums
export const usePaidEnums = () => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ["paidEnums"],
        queryFn: async () => {
            const { data } = await axios.get(
                `${API}/expenses/paid-values/${userInfo._id}`,
                {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                }
            );
            return data;
        },
    });
};
